import { useState } from 'react';
import { apiFetch } from '~/lib/api';
import { Icon } from '~/components/ui';
import { useT } from '~/i18n';
import EuAuthorizationPicker from './EuAuthorizationPicker';
import { readEuAuthorization, type EuWorkAuthorization } from '../lib/immigration';
import {
  buildCountriesPayload,
  offeredCountries,
  readCountries,
  sameCountries,
  toggleCountry,
  type Country,
  type JobPreferences,
} from '../lib/preferences';

/**
 * A qué países se envían las postulaciones: Estados Unidos, Canadá, Europa
 * (cuando el servidor ya publica vacantes de EURES) o varios.
 *
 * Vive en el PANEL y no en Perfil → Preferencias a propósito. Esa pantalla se
 * presenta como lo que leen las agencias, y lo que guarda no mueve el envío;
 * el país SÍ lo mueve (filtra las vacantes del `$vectorSearch`). Ponerlo ahí
 * sería esconder la decisión que más cambia lo que pasa con tus postulaciones
 * en la pantalla que dice que no cambia nada. El panel es donde se ven salir,
 * y a donde la persona vuelve todos los días.
 *
 * Guarda con su propio PATCH, que lleva SOLO `countries` (y, con Europa
 * marcada, `eu_work_authorization`, que también mueve el envío): así el
 * formulario de preferencias no puede mover el envío por accidente, y si el
 * servidor todavía no conociera la clave, lo único que falla es esta tarjeta.
 *
 * Con Europa marcada aparece la pregunta de papeles de la UE: sin papeles (o
 * sin contestar) sólo salen postulaciones a vacantes que aceptan candidatos de
 * fuera de la UE.
 *
 * El botón aparece sólo cuando la selección cambió: la tarjeta, en reposo, es
 * una afirmación ("postulamos en Estados Unidos"), no un formulario pendiente.
 */
interface Props {
  preferences?: JobPreferences | null;
  /** El servidor ya publica vacantes de EURES (`eures_available`): se ofrece Europa. */
  euresAvailable?: boolean;
}

type Notice = { kind: 'saved'; countries: Country[] } | { kind: 'error'; message: string } | null;

export default function ApplyCountriesCard({ preferences, euresAvailable = false }: Props) {
  const t = useT('workspace').dashboard.countries;
  const [saved, setSaved] = useState<Country[]>(() => readCountries(preferences));
  const [selected, setSelected] = useState<Country[]>(saved);
  const [savedEu, setSavedEu] = useState<EuWorkAuthorization | null>(() => readEuAuthorization(preferences));
  const [euAuth, setEuAuth] = useState<EuWorkAuthorization | null>(savedEu);
  const offered = offeredCountries(preferences, euresAvailable);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  // El panel se relee (regreso de Stripe, borradores nuevos): si lo guardado
  // cambió por fuera, la tarjeta lo sigue. Se compara contra lo que el
  // SERVIDOR traía la vez anterior y no contra `saved`: después de guardar
  // acá, el panel puede seguir con la copia vieja hasta que se relea, y eso no
  // puede deshacer lo recién guardado. (Estado derivado en el render, el
  // patrón de React para "ajustar estado cuando cambia una prop".)
  const storedKey = `${readCountries(preferences).join(',')}|${readEuAuthorization(preferences) ?? ''}`;
  const [lastStored, setLastStored] = useState(storedKey);
  if (lastStored !== storedKey) {
    const stored = readCountries(preferences);
    const storedEu = readEuAuthorization(preferences);
    setLastStored(storedKey);
    setSaved(stored);
    setSelected(stored);
    setSavedEu(storedEu);
    setEuAuth(storedEu);
  }

  const euSelected = selected.includes('EU');
  const dirty = !sameCountries(selected, saved) || (euSelected && euAuth !== savedEu);

  const toggle = (country: Country) => {
    setNotice(null);
    setSelected((prev) => toggleCountry(prev, country));
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await apiFetch('/job-apply/api/preferences', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(buildCountriesPayload(selected, euAuth !== savedEu ? euAuth : null)),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNotice({ kind: 'error', message: body?.error || t.saveFailed });
        return;
      }
      // Lo que el servidor dice que quedó manda; si no lo devuelve, lo enviado.
      const stored = Array.isArray(body?.preferences?.countries)
        ? readCountries(body.preferences as JobPreferences)
        : buildCountriesPayload(selected).countries;
      setSaved(stored);
      setSelected(stored);
      if (stored.includes('EU')) {
        const storedEu = readEuAuthorization(body?.preferences as JobPreferences) ?? euAuth;
        setSavedEu(storedEu);
        setEuAuth(storedEu);
      }
      setNotice({ kind: 'saved', countries: stored });
    } catch {
      setNotice({ kind: 'error', message: t.connectionError });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card" aria-labelledby="apply-countries-title">
      <div className="card-body py-4 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-brand mt-0.5">
            <Icon name="send" size={20} />
          </span>
          <div className="space-y-1 min-w-0">
            <h2 id="apply-countries-title" className="font-semibold">{t.title}</h2>
            <p className="text-sm hush">{t.lede}</p>
          </div>
        </div>

        <div role="group" aria-label={t.groupLabel} className="flex flex-wrap gap-x-6 gap-y-2">
          {offered.map((country) => {
            const checked = selected.includes(country);
            // El último marcado no se puede quitar: sin país no hay a dónde postular.
            const locked = checked && selected.length === 1;
            return (
              <label
                key={country}
                className="label cursor-pointer gap-2"
                title={locked ? t.lastOne : undefined}
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary checkbox-sm"
                  checked={checked}
                  disabled={saving || locked}
                  onChange={() => toggle(country)}
                />
                <span className="label-text font-medium">{t.options[country]}</span>
              </label>
            );
          })}
        </div>

        <p className="text-xs hush">{t.caNote}</p>
        {offered.includes('EU') && <p className="text-xs hush">{t.euNote}</p>}

        {euSelected && (
          <EuAuthorizationPicker
            showQuestion
            value={euAuth}
            disabled={saving}
            onChange={(next) => {
              setNotice(null);
              setEuAuth(next);
            }}
          />
        )}

        {dirty && (
          <div className="flex items-center gap-3 flex-wrap">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving && <span className="loading loading-spinner loading-xs" />}
              {t.save}
            </button>
            <span className="text-xs hush">{t.nextBatch}</span>
          </div>
        )}

        {notice?.kind === 'saved' && (
          <p className="text-sm text-data" role="status">
            {t.saved(t.join(notice.countries.map((c) => t.options[c])))}
          </p>
        )}
        {notice?.kind === 'error' && (
          <p className="text-sm" role="alert" style={{ color: 'var(--coral-ink)' }}>
            {notice.message}
          </p>
        )}
      </div>
    </section>
  );
}
